// Boss runtime: one data-driven Boss class (tuning in src/data/bosses.ts) with a
// telegraphed attack library per boss, three phases, weak-point windows, and a
// destruction sequence. Visuals: WARDEN-7 and the Foundry Titan are assembled
// from their rigged part textures; the Singularity Heart is drawn procedurally.

import Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { DEPTH } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { BossApi, BossSystemApi, GameContext, HitArea } from '../../core/contracts';
import { Subscriptions } from '../../core/events';
import type { BossId, DamageInfo, DamageResult, EliteAffix, StatusId, StatusState } from '../../core/types';
import { BOSSES, BOSS_DEATH_SEC, BOSS_INTRO_SEC, BOSS_TRANSITION_SEC, type BossDef } from '../../data/bosses';
import { TITAN_RIG, TITAN_TEX } from './art-titan';
import { WARDEN_RIG, WARDEN_TEX, type Dmg } from './art-warden';

let uid = 900000;

interface Task {
  t: number;
  fn: () => void;
}

interface Laser {
  x0: number;
  y0: number;
  angle: number;
  len: number;
  life: number;
  dmg: number;
  hit: boolean;
  g: Phaser.GameObjects.Graphics;
}

class Boss implements BossApi {
  readonly uid = uid++;
  readonly kind = 'boss' as const;
  readonly elite: readonly EliteAffix[] = [];
  readonly statuses = new Map<StatusId, StatusState>();
  readonly knockbackResist = 1;
  readonly flying: boolean;
  readonly obj: Phaser.GameObjects.Zone;
  readonly body: Phaser.Physics.Arcade.Body;
  readonly name: string;
  readonly title: string;
  readonly maxHp: number;
  readonly scrapValue: number;
  readonly phaseCount = 3;
  hp: number;
  armorHp = 0;
  alive = true;
  spawning = false;
  phase = 1;

  private view: Phaser.GameObjects.Container;
  private parts = new Map<string, Phaser.GameObjects.Image>();
  private facing: 1 | -1 = -1;
  private t = 0;
  private tasks: Task[] = [];
  private lasers: Laser[] = [];
  private busy = 0;
  private rest = BOSS_INTRO_SEC;
  private comboLeft = 0;
  private last = '';
  private cooldowns = new Map<string, number>();
  private invuln = BOSS_INTRO_SEC;
  private weakT = 0;
  private dying = false;
  private stride = 0;
  private hoverX: number;
  private hoverY: number;
  private contactCd = 0;
  private pullT = 0;
  private walkTarget: number | null = null;

  constructor(
    private readonly ctx: GameContext,
    private readonly mgr: BossSystem,
    readonly bossId: BossId,
    private rootX: number,
    private rootY: number,
  ) {
    const def = this.def;
    this.name = def.name;
    this.title = def.title;
    this.maxHp = def.maxHp;
    this.hp = def.maxHp;
    this.scrapValue = def.scrapValue;
    this.flying = def.flying;
    const scene = ctx.scene;
    const size = this.bodySize();
    this.hoverX = rootX;
    this.hoverY = rootY - (bossId === 'foundry_titan' ? 520 : 460);
    const c = this.center();
    this.obj = scene.add.zone(c.x, c.y, size.w, size.h);
    scene.physics.add.existing(this.obj);
    this.body = this.obj.body as Phaser.Physics.Arcade.Body;
    this.body.setAllowGravity(false).setImmovable(true);
    this.view = scene.add.container(rootX, rootY).setDepth(DEPTH.BOSS);
    this.buildView();
  }

  get defId(): BossId {
    return this.bossId;
  }
  get def(): BossDef {
    return BOSSES[this.bossId];
  }
  get x(): number {
    return this.body.center.x;
  }
  get y(): number {
    return this.body.center.y;
  }
  get radius(): number {
    return this.def.radius;
  }
  private get tuning() {
    return this.def.phases[this.phase - 1];
  }
  private get dmg(): Dmg {
    return Math.min(2, this.phase - 1) as Dmg;
  }

  private bodySize(): { w: number; h: number } {
    if (this.bossId === 'warden') return { w: 190, h: 250 };
    if (this.bossId === 'foundry_titan') return { w: 260, h: 170 };
    return { w: 150, h: 150 };
  }

  private center(): { x: number; y: number } {
    if (this.bossId === 'warden') return { x: this.rootX, y: this.rootY - 210 };
    return { x: this.hoverX, y: this.hoverY };
  }

  // ───────────── visuals ─────────────

  private img(name: string, key: string, ox = 0.5, oy = 0.5, add = false): Phaser.GameObjects.Image {
    const scene = this.ctx.scene;
    const k = scene.textures.exists(key) ? key : TEX.soft;
    const im = scene.add.image(0, 0, k).setOrigin(ox, oy);
    if (add) im.setBlendMode(Phaser.BlendModes.ADD);
    if (k === TEX.soft) im.setTint(this.def.color).setScale(2);
    this.view.add(im);
    this.parts.set(name, im);
    return im;
  }

  private buildView(): void {
    if (this.bossId === 'warden') {
      const T = WARDEN_RIG.tex as Record<string, { w: number; h: number; ox: number; oy: number }>;
      const o = (k: string): [number, number] => (T[k] ? [T[k].ox / T[k].w, T[k].oy / T[k].h] : [0.5, 0.5]);
      for (const side of ['R', 'F']) {
        this.img(`thigh${side}`, WARDEN_TEX.thigh, ...o('thigh'));
        this.img(`shin${side}`, WARDEN_TEX.shin, ...o('shin'));
        this.img(`foot${side}`, WARDEN_TEX.foot, ...o('foot'));
        if (side === 'R') {
          this.img('rackR', WARDEN_TEX.rack(0), ...o('rack'));
          this.img('antenna', WARDEN_TEX.antenna, ...o('antenna'));
          this.img('pelvis', WARDEN_TEX.pelvis(0), ...o('pelvis'));
          this.img('torso', WARDEN_TEX.torso(0), ...o('torso'));
          this.img('coreGlow', WARDEN_TEX.coreGlow, ...o('coreGlow'), true);
          this.img('core', WARDEN_TEX.core, ...o('core'));
          this.img('shutterTop', WARDEN_TEX.shutterTop, ...o('shutterTop'));
          this.img('shutterBot', WARDEN_TEX.shutterBot, ...o('shutterBot'));
          this.img('head', WARDEN_TEX.head(0), ...o('head'));
          this.img('headGlow', WARDEN_TEX.headGlow, ...o('headGlow'), true);
          this.img('rackF', WARDEN_TEX.rack(0), ...o('rack'));
        }
      }
      for (const k of ['thighR', 'shinR', 'footR', 'rackR']) this.parts.get(k)?.setTint(0x9aa3b4);
    } else if (this.bossId === 'foundry_titan') {
      this.img('thrusterR', TITAN_TEX.thruster);
      this.img('flameR', TITAN_TEX.flame, 0.5, 0, true);
      this.img('crucible', TITAN_TEX.crucible(0), 0.5, 0.1);
      this.img('heartGlow', TITAN_TEX.heartGlow, 0.5, 0.5, true);
      this.img('heart', TITAN_TEX.heart);
      this.img('hull', TITAN_TEX.hull(0));
      this.img('hullGlow', TITAN_TEX.hullGlow, 0.5, 0.5, true);
      this.img('thrusterF', TITAN_TEX.thruster);
      this.img('flameF', TITAN_TEX.flame, 0.5, 0, true);
    } else {
      // Singularity Heart: procedural core + four orbiting shield plates.
      this.ensureHeartTextures();
      this.img('halo', 'boss_heart_halo', 0.5, 0.5, true);
      this.img('core', 'boss_heart_core');
      for (let i = 0; i < 4; i++) this.img(`plate${i}`, 'boss_heart_plate');
    }
  }

  private ensureHeartTextures(): void {
    const scene = this.ctx.scene;
    if (!scene.textures.exists('boss_heart_core')) {
      const g = scene.make.graphics({}, false);
      g.fillStyle(COLORS.ink, 1).fillCircle(70, 70, 66);
      g.fillStyle(0x2a0f3a, 1).fillCircle(70, 70, 58);
      g.fillStyle(COLORS.orbUnstable, 1).fillCircle(70, 70, 34);
      g.fillStyle(0xffffff, 1).fillCircle(70, 70, 14);
      g.lineStyle(4, 0x9b6bff, 1).strokeCircle(70, 70, 50);
      g.generateTexture('boss_heart_core', 140, 140);
      g.clear();
      g.fillStyle(COLORS.ink, 1).fillRoundedRect(0, 0, 60, 130, 14);
      g.fillStyle(0x23293a, 1).fillRoundedRect(5, 5, 50, 120, 11);
      g.fillStyle(0x6b4fa8, 1).fillRect(26, 18, 8, 94);
      g.lineStyle(2, 0x9b6bff, 1).strokeRoundedRect(5, 5, 50, 120, 11);
      g.generateTexture('boss_heart_plate', 60, 130);
      g.clear();
      for (let r = 120; r > 0; r -= 8) g.fillStyle(0xffffff, 0.035).fillCircle(120, 120, r);
      g.generateTexture('boss_heart_halo', 240, 240);
      g.destroy();
    }
  }

  private part(name: string): Phaser.GameObjects.Image | undefined {
    return this.parts.get(name);
  }

  private poseWarden(dt: number): void {
    const R = WARDEN_RIG;
    const moving = this.walkTarget !== null;
    if (moving) this.stride += dt * 3.2 * this.tuning.speed;
    const bob = Math.sin(this.stride * 2) * (moving ? 5 : 2) + Math.sin(this.t * 1.6) * 2;
    const hip = { x: 0, y: -R.hipHeight + bob };
    const lean = this.busy > 0 ? 0.04 : moving ? 0.07 : 0.02;
    const legs: ['F' | 'R', { x: number; y: number }, number, number][] = [
      ['R', R.hipRear, R.footRear, Math.PI],
      ['F', R.hipFront, R.footFront, 0],
    ];
    for (const [side, hj, footX, ph] of legs) {
      const s = this.stride + ph;
      const lift = moving ? Math.max(0, Math.sin(s)) * 26 : 0;
      const ankle = { x: footX + (moving ? Math.cos(s) * 22 : 0), y: -R.ankleHeight - lift };
      const h = { x: hip.x + hj.x, y: hip.y + hj.y };
      const knee = ik(h, ankle, R.thighLen, R.shinLen);
      this.part(`thigh${side}`)?.setPosition(h.x, h.y).setRotation(Math.atan2(knee.y - h.y, knee.x - h.x) - Math.PI / 2);
      this.part(`shin${side}`)?.setPosition(knee.x, knee.y).setRotation(Math.atan2(ankle.y - knee.y, ankle.x - knee.x) - Math.PI / 2);
      this.part(`foot${side}`)?.setPosition(ankle.x, ankle.y);
    }
    const tp = { x: hip.x + R.torsoPivot.x, y: hip.y + R.torsoPivot.y };
    const rot = (p: { x: number; y: number }) => ({ x: tp.x + p.x * Math.cos(lean) - p.y * Math.sin(lean), y: tp.y + p.x * Math.sin(lean) + p.y * Math.cos(lean) });
    this.part('pelvis')?.setPosition(hip.x, hip.y).setTexture(this.safeKey(WARDEN_TEX.pelvis(this.dmg)));
    this.part('torso')?.setPosition(tp.x, tp.y).setRotation(lean).setTexture(this.safeKey(WARDEN_TEX.torso(this.dmg)));
    const core = rot(R.core);
    const open = this.weakT > 0 ? R.shutterTravel : 0;
    this.part('core')?.setPosition(core.x, core.y);
    this.part('coreGlow')?.setPosition(core.x, core.y).setAlpha(0.4 + (this.weakT > 0 ? 0.6 : 0.2 * Math.sin(this.t * 4)));
    this.part('shutterTop')?.setPosition(core.x, core.y - open).setRotation(lean);
    this.part('shutterBot')?.setPosition(core.x, core.y + open).setRotation(lean);
    const neck = rot(R.neck);
    this.part('head')?.setPosition(neck.x, neck.y).setRotation(lean * 0.5).setTexture(this.safeKey(WARDEN_TEX.head(this.dmg)));
    this.part('headGlow')?.setPosition(neck.x, neck.y).setRotation(lean * 0.5).setAlpha(0.7 + 0.3 * Math.sin(this.t * 6));
    const rr = rot(R.rackRear);
    const rf = rot(R.rackFront);
    this.part('rackR')?.setPosition(rr.x, rr.y).setRotation(lean).setTexture(this.safeKey(WARDEN_TEX.rack(this.dmg)));
    this.part('rackF')?.setPosition(rf.x, rf.y).setRotation(lean).setTexture(this.safeKey(WARDEN_TEX.rack(this.dmg)));
    const an = rot(R.antenna);
    this.part('antenna')?.setPosition(an.x, an.y).setRotation(lean + Math.sin(this.t * 2) * 0.05);
  }

  private safeKey(k: string): string {
    return this.ctx.scene.textures.exists(k) ? k : TEX.soft;
  }

  /** World position of the warden lens / titan spout / heart core (attack origins). */
  private emitter(): { x: number; y: number } {
    if (this.bossId === 'warden') {
      const R = WARDEN_RIG;
      return { x: this.rootX + this.facing * (R.neck.x + R.lens.x - 10), y: this.rootY - R.hipHeight + R.torsoPivot.y + R.neck.y + R.lens.y };
    }
    if (this.bossId === 'foundry_titan') return { x: this.hoverX + this.facing * TITAN_RIG.spout.x * 0.6, y: this.hoverY + TITAN_RIG.cruciblePivot.y + 40 };
    return { x: this.hoverX, y: this.hoverY };
  }

  private weakPoint(): HitArea {
    if (this.bossId === 'warden') {
      const R = WARDEN_RIG;
      const cx = this.rootX + this.facing * (R.torsoPivot.x + R.core.x);
      const cy = this.rootY - R.hipHeight + R.torsoPivot.y + R.core.y;
      return { x: cx - 40, y: cy - 40, w: 80, h: 80, mult: this.def.weakMult };
    }
    if (this.bossId === 'foundry_titan') {
      const cx = this.hoverX + TITAN_RIG.heart.x;
      const cy = this.hoverY + TITAN_RIG.heart.y;
      return { x: cx - 45, y: cy - 45, w: 90, h: 90, mult: this.def.weakMult };
    }
    return { x: this.hoverX - 45, y: this.hoverY - 45, w: 90, h: 90, mult: this.def.weakMult };
  }

  // ───────────── EnemyApi ─────────────

  hitAreas(): readonly HitArea[] {
    if (this.dying) return [];
    const b = this.body;
    const areas: HitArea[] = [{ x: b.x, y: b.y, w: b.width, h: b.height, mult: 1 }];
    if (this.weakT > 0) areas.unshift(this.weakPoint());
    return areas;
  }

  /** Heart plates block shots unless the core is exposed. */
  blocks(fromX: number, fromY: number): boolean {
    if (this.bossId !== 'singularity_heart' || this.weakT > 0 || this.dying) return false;
    const ang = Math.atan2(fromY - this.y, fromX - this.x);
    for (let i = 0; i < 4; i++) {
      const a = this.plateAngle(i);
      if (Math.abs(Phaser.Math.Angle.Wrap(ang - a)) < 0.42) return true;
    }
    return false;
  }

  private plateAngle(i: number): number {
    return this.t * (0.9 + 0.3 * this.phase) + (i * Math.PI) / 2;
  }

  receiveDamage(amount: number, info: DamageInfo): DamageResult {
    if (!this.alive || this.dying || this.invuln > 0) return { dealt: 0, killed: false, blocked: true, absorbed: 0 };
    const floor = this.phase < 3 ? this.maxHp * this.def.phaseThresholds[this.phase - 1] : 0;
    const before = this.hp;
    this.hp = Math.max(this.phase < 3 ? floor : 0, this.hp - amount);
    const dealt = before - this.hp;
    if (!info.isDot) for (const p of this.parts.values()) if (!p.blendMode) this.ctx.fx.hitFlash(p, 50);
    if (this.phase < 3 && this.hp <= floor) this.nextPhase();
    const killed = this.hp <= 0;
    if (killed) this.startDeath();
    return { dealt, killed, blocked: false, absorbed: 0 };
  }

  knockback(): void {}
  pull(): void {}

  private nextPhase(): void {
    const ctx = this.ctx;
    this.phase++;
    this.invuln = BOSS_TRANSITION_SEC;
    this.busy = BOSS_TRANSITION_SEC;
    this.cancelAll();
    ctx.projectiles.clear('enemy');
    ctx.time.hitstop(120);
    ctx.audio.play('boss_phase');
    ctx.fx.ring(this.x, this.y, 420, { color: this.def.glow, duration: 500, width: 10 });
    ctx.fx.shake(0.012, 400);
    ctx.fx.flash(this.def.glow, 0.22, 220);
    const p = ctx.player;
    if (p) p.applyKnockback(Math.sign(p.x - this.x || 1) * 900, -420);
    ctx.bus.emit('boss:phase', { boss: this, phase: this.phase });
    ctx.bus.emit('ui:banner', { title: `PHASE ${this.phase}`, subtitle: this.name, color: this.def.glow });
  }

  private startDeath(): void {
    if (this.dying) return;
    this.dying = true;
    this.alive = false;
    this.cancelAll();
    const ctx = this.ctx;
    ctx.projectiles.clear('enemy');
    ctx.time.slowMo(0.35, 900);
    ctx.audio.play('boss_death');
    this.body.enable = false;
    const n = 9;
    for (let i = 0; i < n; i++) {
      this.after((i / n) * BOSS_DEATH_SEC * 0.85, () => {
        const x = this.x + (Math.random() - 0.5) * this.bodySize().w;
        const y = this.y + (Math.random() - 0.5) * this.bodySize().h;
        ctx.fx.explosion(x, y, 60 + Math.random() * 60, i % 2 ? COLORS.threatHot : this.def.glow);
        ctx.fx.shake(0.008, 160);
      });
    }
    this.after(BOSS_DEATH_SEC, () => {
      ctx.fx.explosion(this.x, this.y, 220, COLORS.explosive);
      ctx.fx.flash(0xffffff, 0.5, 400);
      ctx.fx.shake(0.02, 500);
      ctx.run.stats.bossKills += 1;
      ctx.loot.spawnScrap(this.x, this.y, this.scrapValue);
      ctx.bus.emit('boss:defeated', { boss: this });
      ctx.scene.tweens.add({ targets: this.view, alpha: 0, duration: 500, onComplete: () => this.view.destroy() });
      this.mgr.finish(this);
    });
  }

  // ───────────── scheduling ─────────────

  private after(sec: number, fn: () => void): void {
    this.tasks.push({ t: sec, fn });
  }

  private cancelAll(): void {
    this.tasks = [];
    for (const l of this.lasers) l.g.destroy();
    this.lasers = [];
    this.walkTarget = null;
  }

  // ───────────── behaviour ─────────────

  update(dt: number): void {
    this.t += dt;
    for (let i = this.tasks.length - 1; i >= 0; i--) {
      const task = this.tasks[i];
      task.t -= dt;
      if (task.t <= 0) {
        this.tasks.splice(i, 1);
        task.fn();
      }
    }
    this.updateLasers(dt);
    if (this.invuln > 0) this.invuln -= dt;
    if (this.weakT > 0) this.weakT -= dt;
    if (this.contactCd > 0) this.contactCd -= dt;
    if (!this.dying) {
      this.think(dt);
      this.contact();
    }
    this.syncBody();
    this.pose(dt);
  }

  private think(dt: number): void {
    const p = this.ctx.player;
    if (!p) return;
    if (p.alive) this.facing = p.x < this.x ? -1 : 1;
    for (const [k, v] of this.cooldowns) this.cooldowns.set(k, v - dt);
    this.move(dt);
    if (this.busy > 0) {
      this.busy -= dt;
      return;
    }
    this.rest -= dt;
    if (this.rest > 0 || !p.alive) return;
    if (this.comboLeft <= 0) {
      const [a, b] = this.tuning.combo;
      this.comboLeft = this.ctx.rng.int(a, b);
    }
    const choices = this.def.attacks.filter((a) => a.minPhase <= this.phase && (this.cooldowns.get(a.id) ?? 0) <= 0 && a.id !== this.last);
    const pick = this.ctx.rng.weighted(choices.length ? choices : this.def.attacks.filter((a) => a.minPhase <= this.phase), (a) => a.weight);
    if (!pick) return;
    this.last = pick.id;
    this.cooldowns.set(pick.id, pick.cooldown);
    const dur = this.attack(pick.id);
    this.busy = dur + 0.2;
    this.comboLeft--;
    if (this.comboLeft <= 0) {
      // End of combo: vent the weak point, then rest.
      this.rest = this.tuning.rest;
      this.after(dur, () => this.vent());
    } else this.rest = this.tuning.recovery * 0.5;
  }

  private vent(): void {
    if (this.dying) return;
    this.weakT = this.def.weakWindow;
    this.busy = Math.max(this.busy, this.def.weakWindow * 0.8);
    const w = this.weakPoint();
    this.ctx.fx.warnMarker(w.x + w.w / 2, w.y - 30, this.def.weakWindow * 1000, { color: COLORS.uiGood });
    this.ctx.audio.play('vent');
  }

  private move(dt: number): void {
    const p = this.ctx.player;
    const W = this.ctx.world.width;
    const margin = 260;
    if (this.bossId === 'warden') {
      const want = this.busy > 0 ? null : Phaser.Math.Clamp(p.x - this.facing * 300, margin, W - margin);
      this.walkTarget = want !== null && Math.abs(want - this.rootX) > 40 ? want : null;
      if (this.walkTarget !== null) this.rootX += Math.sign(this.walkTarget - this.rootX) * this.tuning.move * dt;
      this.view.setPosition(this.rootX, this.rootY).setScale(this.facing, 1);
    } else {
      const tx = Phaser.Math.Clamp(p.x + Math.sin(this.t * 0.5) * 300, margin, W - margin);
      const ty = (this.bossId === 'foundry_titan' ? 330 : 420) + Math.sin(this.t * 1.3) * 30;
      const k = 1 - Math.exp(-dt * (this.busy > 0 ? 0.6 : 1.2));
      this.hoverX += (tx - this.hoverX) * k * (this.tuning.move / 120);
      this.hoverY += (ty - this.hoverY) * k;
      this.view.setPosition(this.hoverX, this.hoverY).setScale(this.bossId === 'foundry_titan' ? this.facing : 1, 1);
    }
    if (this.pullT > 0 && p.alive) {
      this.pullT -= dt;
      const dx = this.x - p.x;
      const dy = this.y - p.y;
      const d = Math.hypot(dx, dy) || 1;
      if (d < 620) p.applyKnockback((dx / d) * 1500 * dt, (dy / d) * 900 * dt);
    }
  }

  private syncBody(): void {
    const c = this.bossId === 'warden' ? { x: this.rootX, y: this.rootY - 210 } : { x: this.hoverX, y: this.hoverY };
    this.obj.setPosition(c.x, c.y);
    this.body.reset(c.x, c.y);
  }

  private pose(dt: number): void {
    if (this.bossId === 'warden') this.poseWarden(dt);
    else if (this.bossId === 'foundry_titan') {
      const R = TITAN_RIG;
      const sway = Math.sin(this.t * 1.4) * 0.06;
      this.part('hull')?.setTexture(this.safeKey(TITAN_TEX.hull(this.dmg)));
      this.part('crucible')?.setPosition(R.cruciblePivot.x, R.cruciblePivot.y).setRotation(sway).setTexture(this.safeKey(TITAN_TEX.crucible(this.dmg)));
      this.part('heart')?.setPosition(R.heart.x, R.heart.y).setAlpha(this.weakT > 0 ? 1 : 0.35);
      this.part('heartGlow')?.setPosition(R.heart.x, R.heart.y).setAlpha(this.weakT > 0 ? 0.9 : 0.25);
      for (const [s, m] of [['R', R.thrusterRear], ['F', R.thrusterFront]] as const) {
        this.part(`thruster${s}`)?.setPosition(m.x, m.y);
        this.part(`flame${s}`)?.setPosition(m.x + R.flameMount.x, m.y + R.flameMount.y).setScale(1, 0.8 + 0.3 * Math.random());
      }
      this.part('hullGlow')?.setAlpha(0.5 + 0.3 * Math.sin(this.t * 3));
    } else {
      const exposed = this.weakT > 0;
      const r = exposed ? 150 : 110 + 10 * this.phase;
      for (let i = 0; i < 4; i++) {
        const a = this.plateAngle(i);
        this.part(`plate${i}`)?.setPosition(Math.cos(a) * r, Math.sin(a) * r).setRotation(a).setAlpha(exposed ? 0.6 : 1);
      }
      this.part('core')?.setScale(1 + 0.06 * Math.sin(this.t * 5)).setRotation(this.t * 0.4);
      this.part('halo')?.setTint(this.def.color).setScale(1.6 + 0.15 * Math.sin(this.t * 3)).setAlpha(exposed ? 0.9 : 0.5);
    }
    this.view.setAlpha(this.invuln > 0 && !this.dying && this.t > BOSS_INTRO_SEC ? 0.75 + 0.25 * Math.sin(this.t * 30) : 1);
  }

  private contact(): void {
    const p = this.ctx.player;
    if (!p?.alive || this.contactCd > 0) return;
    const b = this.body;
    const pb = p.body;
    if (b.right < pb.left || b.left > pb.right || b.bottom < pb.top || b.top > pb.bottom) return;
    const res = this.ctx.damage.hitPlayer({ amount: this.def.damage.contact ?? 14, tags: [], source: 'enemy', sourceX: this.x, sourceY: this.y, knockback: 800, label: 'boss-contact' });
    if (!res.blocked) this.contactCd = 0.8;
  }

  private hurt(amount: number, x: number, y: number, kb = 500, label = 'boss'): void {
    this.ctx.damage.hitPlayer({ amount, tags: [], source: 'enemy', sourceX: x, sourceY: y, knockback: kb, label });
  }

  private playerRect() {
    const b = this.ctx.player.body;
    return { x: b.left, y: b.top, w: b.width, h: b.height };
  }

  // ───────────── attack library ─────────────

  /** Starts an attack; returns its duration in seconds. */
  private attack(id: string): number {
    const tell = this.tuning.tell;
    switch (id) {
      case 'sweep':
        return this.sweepLaser(tell);
      case 'mortar':
        return this.barrage(tell, 4 + this.phase, 'mortar');
      case 'stomp':
        return this.stomp(tell);
      case 'drones':
        return this.drones();
      case 'pour':
        return this.pour(tell);
      case 'grapple':
        return this.grapple(tell);
      case 'slag':
        return this.ring(tell, 14 + this.phase * 4, 'slag');
      case 'hammer':
        return this.hammer(tell);
      case 'flame':
        return this.sweepLaser(tell, COLORS.burn);
      case 'well':
        return this.well(tell);
      case 'spiral':
        return this.spiral(tell);
      case 'cross':
        return this.cross(tell);
      case 'blink':
        return this.blink(tell);
      default:
        return 0.5;
    }
  }

  private sweepLaser(tell: number, color: number = COLORS.threat): number {
    const ctx = this.ctx;
    const o = this.emitter();
    const dir = this.facing;
    const start = dir > 0 ? -0.15 : Math.PI + 0.15;
    const end = dir > 0 ? 0.75 : Math.PI - 0.75;
    const warn = 0.9 * tell;
    const tele = ctx.fx.warnLine(o.x, o.y, o.x + Math.cos(start) * 1800, o.y + Math.sin(start) * 1800, warn * 1000, { width: 10, color });
    ctx.audio.play('enemy_laser_charge');
    const sweep = 1.3 / this.tuning.speed;
    this.after(warn, () => {
      tele.cancel();
      ctx.audio.play('enemy_laser_fire');
      const g = ctx.scene.add.graphics().setDepth(DEPTH.PROJECTILES).setBlendMode(Phaser.BlendModes.ADD);
      const l: Laser = { x0: o.x, y0: o.y, angle: start, len: 2000, life: sweep, dmg: this.def.damage.laser ?? this.def.damage.flame ?? 20, hit: false, g };
      this.lasers.push(l);
      const t0 = this.t;
      const spin = (end - start) / sweep;
      (l as Laser & { spin: number; t0: number }).spin = spin;
      (l as Laser & { spin: number; t0: number }).t0 = t0;
    });
    return warn + sweep;
  }

  private updateLasers(dt: number): void {
    for (let i = this.lasers.length - 1; i >= 0; i--) {
      const l = this.lasers[i] as Laser & { spin?: number };
      l.life -= dt;
      if (l.life <= 0) {
        l.g.destroy();
        this.lasers.splice(i, 1);
        continue;
      }
      if (l.spin) l.angle += l.spin * dt;
      const o = this.bossId === 'singularity_heart' ? { x: this.x, y: this.y } : this.emitter();
      l.x0 = o.x;
      l.y0 = o.y;
      // Stop the beam at walls.
      const hit = this.ctx.world.raycast(l.x0, l.y0, l.x0 + Math.cos(l.angle) * l.len, l.y0 + Math.sin(l.angle) * l.len);
      const x1 = hit.x;
      const y1 = hit.y;
      l.g.clear();
      l.g.lineStyle(26, COLORS.threat, 0.25).lineBetween(l.x0, l.y0, x1, y1);
      l.g.lineStyle(12, COLORS.threatHot, 0.7).lineBetween(l.x0, l.y0, x1, y1);
      l.g.lineStyle(4, 0xffffff, 1).lineBetween(l.x0, l.y0, x1, y1);
      if (Math.random() < 0.5) this.ctx.fx.sparks(x1, y1, l.angle + Math.PI, { count: 2, color: COLORS.threatHot });
      if (!l.hit && this.ctx.player.alive && segRect(l.x0, l.y0, x1, y1, 10, this.playerRect())) {
        l.hit = true;
        this.hurt(l.dmg, l.x0, l.y0, 600, 'laser');
      }
    }
  }

  private barrage(tell: number, count: number, kind: string): number {
    const ctx = this.ctx;
    const p = ctx.player;
    const floor = ctx.world.groundBelow(p.x, p.y, 900) ?? this.rootY;
    const warn = 0.9 * tell;
    ctx.audio.play('enemy_telegraph');
    for (let i = 0; i < count; i++) {
      const x = Phaser.Math.Clamp(p.x + (i - (count - 1) / 2) * 150 + ctx.rng.float(-50, 50), 80, ctx.world.width - 80);
      const y = ctx.world.groundBelow(x, floor - 300, 900) ?? floor;
      const delay = i * 0.12;
      this.after(delay, () => ctx.fx.warnCircle(x, y, 95, warn * 1000));
      this.after(delay + warn, () => {
        ctx.damage.explode(x, y, 95, { amount: this.def.damage[kind] ?? 16, tags: ['explosive'], source: 'enemy', knockback: 520, label: kind }, { hurtsPlayer: true, color: COLORS.threatHot });
      });
    }
    return warn + count * 0.12 + 0.3;
  }

  private stomp(tell: number): number {
    const ctx = this.ctx;
    const warn = 0.6 * tell;
    const y = this.rootY;
    const W = ctx.world.width;
    ctx.fx.warnRect(48, y - 60, W - 96, 60, warn * 1000);
    ctx.audio.play('enemy_telegraph', { pitch: 0.6 });
    this.after(warn, () => {
      ctx.audio.play('boss_slam');
      ctx.fx.shake(0.012, 300);
      ctx.fx.ring(this.rootX, y, 220, { color: COLORS.threatHot, duration: 350 });
      for (const dir of [-1, 1]) {
        ctx.projectiles.spawn({ owner: 'enemy', x: this.rootX + dir * 80, y: y - 26, angle: dir > 0 ? 0 : Math.PI, speed: 640 * this.tuning.speed, damage: this.def.damage.wave ?? 16, tags: [], texture: TEX.projEnemyOrb, radius: 26, lifetime: 3.2, scale: 1.6, glow: true, tint: COLORS.threatHot, knockback: 500 });
      }
    });
    return warn + 0.5;
  }

  private drones(): number {
    const ctx = this.ctx;
    ctx.audio.play('boss_roar', { volume: 0.6 });
    for (const dx of [-260, 260]) ctx.enemies.spawn('hornet', Phaser.Math.Clamp(this.x + dx, 120, ctx.world.width - 120), this.y - 220, { portalMs: 800, level: this.phase });
    return 0.9;
  }

  private pour(tell: number): number {
    const ctx = this.ctx;
    const p = ctx.player;
    const warn = 0.8 * tell;
    const cols = 3 + this.phase;
    for (let i = 0; i < cols; i++) {
      const x = Phaser.Math.Clamp(p.x + (i - (cols - 1) / 2) * 180, 100, ctx.world.width - 100);
      ctx.fx.warnRect(x - 40, 48, 80, ctx.world.height - 140, warn * 1000, { color: COLORS.burn });
      this.after(warn + i * 0.05, () => {
        for (let k = 0; k < 3; k++) ctx.projectiles.spawn({ owner: 'enemy', x: x + ctx.rng.float(-20, 20), y: 70 - k * 60, angle: Math.PI / 2, speed: 900, damage: this.def.damage.molten ?? 18, tags: ['burn'], texture: TEX.projEnemyOrb, radius: 20, lifetime: 2, scale: 1.3, glow: true, tint: COLORS.burn, gravity: 600 });
      });
    }
    ctx.audio.play('enemy_telegraph');
    return warn + 0.8;
  }

  private grapple(tell: number): number {
    const ctx = this.ctx;
    const o = this.emitter();
    const p = ctx.player;
    const ang = Math.atan2(p.y - o.y, p.x - o.x);
    const warn = 0.7 * tell;
    ctx.fx.warnLine(o.x, o.y, o.x + Math.cos(ang) * 1400, o.y + Math.sin(ang) * 1400, warn * 1000, { width: 8 });
    this.after(warn, () => {
      const hit = ctx.world.raycast(o.x, o.y, o.x + Math.cos(ang) * 1400, o.y + Math.sin(ang) * 1400);
      ctx.fx.beam(o.x, o.y, hit.x, hit.y, { color: COLORS.steel4, width: 6, duration: 200 });
      if (p.alive && segRect(o.x, o.y, hit.x, hit.y, 14, this.playerRect()) && !p.invulnerable) {
        this.hurt(this.def.damage.grapple ?? 14, o.x, o.y, 0, 'grapple');
        p.applyKnockback(Math.cos(ang) * -1300, Math.sin(ang) * -900);
      }
    });
    return warn + 0.4;
  }

  private ring(tell: number, n: number, kind: string): number {
    const ctx = this.ctx;
    const warn = 0.6 * tell;
    ctx.fx.warnCircle(this.x, this.y, 140, warn * 1000);
    this.after(warn, () => {
      const off = ctx.rng.float(0, Math.PI);
      for (let i = 0; i < n; i++) {
        const a = off + (i / n) * Math.PI * 2;
        ctx.projectiles.spawn({ owner: 'enemy', x: this.x, y: this.y, angle: a, speed: 380 * this.tuning.speed, damage: this.def.damage[kind] ?? 12, tags: [], texture: TEX.projEnemyOrb, radius: 14, lifetime: 4, glow: true, tint: this.def.color });
      }
      ctx.audio.play('enemy_shoot');
    });
    return warn + 0.4;
  }

  private hammer(tell: number): number {
    const ctx = this.ctx;
    const p = ctx.player;
    const x = p.x;
    const y = ctx.world.groundBelow(x, p.y, 900) ?? p.feetY;
    const warn = 0.8 * tell;
    ctx.fx.warnCircle(x, y, 150, warn * 1000);
    this.after(warn * 0.5, () => {
      this.hoverX = x;
    });
    this.after(warn, () => {
      ctx.damage.explode(x, y, 150, { amount: this.def.damage.hammer ?? 26, tags: ['explosive'], source: 'enemy', knockback: 800, label: 'hammer' }, { hurtsPlayer: true, color: COLORS.burn });
      ctx.audio.play('boss_slam');
      ctx.fx.shake(0.014, 300);
      this.after(0.2, () => this.vent());
    });
    return warn + 0.8;
  }

  private well(tell: number): number {
    const ctx = this.ctx;
    const warn = 0.5 * tell;
    ctx.fx.warnCircle(this.x, this.y, 620, (warn + 2.4) * 1000, { color: COLORS.gravity });
    ctx.audio.play('gravity_field');
    this.after(warn, () => {
      this.pullT = 2.4;
    });
    this.after(warn + 2.4, () => this.ring(0.3, 10, 'burst'));
    return warn + 2.8;
  }

  private spiral(tell: number): number {
    const ctx = this.ctx;
    const warn = 0.5 * tell;
    const arms = 3 + this.phase;
    const dur = 2.4;
    ctx.fx.warnMarker(this.x, this.y - 140, warn * 1000);
    const steps = Math.round(dur / 0.12);
    for (let s = 0; s < steps; s++) {
      this.after(warn + s * 0.12, () => {
        const base = s * (0.22 + 0.04 * this.phase);
        for (let a = 0; a < arms; a++) {
          const ang = base + (a / arms) * Math.PI * 2;
          ctx.projectiles.spawn({ owner: 'enemy', x: this.x, y: this.y, angle: ang, speed: 300 * this.tuning.speed, damage: this.def.damage.orb ?? 12, tags: [], texture: TEX.projEnemyOrb, radius: 13, lifetime: 4.5, glow: true, tint: COLORS.orbUnstable });
        }
      });
    }
    return warn + dur;
  }

  private cross(tell: number): number {
    const ctx = this.ctx;
    const warn = 1.0 * tell;
    const off = ctx.rng.float(0, Math.PI / 4);
    const teles = [0, 1, 2, 3].map((i) => {
      const a = off + (i * Math.PI) / 2;
      return ctx.fx.warnLine(this.x, this.y, this.x + Math.cos(a) * 1800, this.y + Math.sin(a) * 1800, warn * 1000, { width: 8 });
    });
    ctx.audio.play('enemy_laser_charge');
    this.after(warn, () => {
      teles.forEach((t) => t.cancel());
      ctx.audio.play('enemy_laser_fire');
      for (let i = 0; i < 4; i++) {
        const g = ctx.scene.add.graphics().setDepth(DEPTH.PROJECTILES).setBlendMode(Phaser.BlendModes.ADD);
        const l = { x0: this.x, y0: this.y, angle: off + (i * Math.PI) / 2, len: 2000, life: 0.9, dmg: this.def.damage.laser ?? 22, hit: false, g, spin: this.phase >= 3 ? 0.5 : 0.25 };
        this.lasers.push(l);
      }
    });
    return warn + 1;
  }

  private blink(tell: number): number {
    const ctx = this.ctx;
    const W = ctx.world.width;
    const nx = Phaser.Math.Clamp(ctx.player.x + ctx.rng.sign() * ctx.rng.float(300, 600), 200, W - 200);
    const ny = ctx.rng.float(260, 520);
    const warn = 0.6 * tell;
    ctx.fx.warnMarker(nx, ny, warn * 1000, { color: COLORS.orbUnstable });
    ctx.fx.ring(this.x, this.y, 160, { color: COLORS.orbUnstable, duration: warn * 1000 });
    this.after(warn, () => {
      ctx.fx.ring(this.x, this.y, 200, { color: COLORS.orbUnstable, duration: 200 });
      this.hoverX = nx;
      this.hoverY = ny;
      ctx.fx.ring(nx, ny, 200, { color: COLORS.orbUnstable, duration: 250, fromRadius: 10 });
      ctx.audio.play('phase_echo');
      if (this.phase >= 2) this.ring(0.2, 8, 'burst');
    });
    return warn + 0.4;
  }

  intro(): void {
    const ctx = this.ctx;
    ctx.audio.play('boss_roar');
    ctx.fx.shake(0.01, 600);
    ctx.bus.emit('ui:banner', { title: this.name, subtitle: this.title.toUpperCase(), color: this.def.glow });
  }

  destroy(): void {
    this.cancelAll();
    this.view.destroy();
    this.obj.destroy();
  }
}

function ik(a: { x: number; y: number }, b: { x: number; y: number }, l1: number, l2: number): { x: number; y: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d = Math.min(l1 + l2 - 0.01, Math.max(Math.abs(l1 - l2) + 0.01, Math.hypot(dx, dy)));
  const ang = Math.atan2(dy, dx);
  const k = Math.acos(Phaser.Math.Clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1));
  // Reverse-jointed (digitigrade) knee bends backward.
  const t = ang + k;
  return { x: a.x + Math.cos(t) * l1, y: a.y + Math.sin(t) * l1 };
}

function segRect(x0: number, y0: number, x1: number, y1: number, half: number, r: { x: number; y: number; w: number; h: number }): boolean {
  const rx = r.x - half;
  const ry = r.y - half;
  const rw = r.w + half * 2;
  const rh = r.h + half * 2;
  let t0 = 0;
  let t1 = 1;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) {
      if (t > t1) return false;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return false;
      if (t < t1) t1 = t;
    }
    return true;
  };
  return clip(-dx, x0 - rx) && clip(dx, rx + rw - x0) && clip(-dy, y0 - ry) && clip(dy, ry + rh - y0);
}

class BossSystem implements BossSystemApi {
  private boss: Boss | null = null;
  private subs = new Subscriptions();

  constructor(private readonly ctx: GameContext) {}

  get active(): BossApi | null {
    return this.boss && (this.boss.alive || this.boss.hp > 0) ? this.boss : null;
  }

  spawn(id: BossId, x: number, y: number): BossApi {
    const b = new Boss(this.ctx, this, BOSSES[id] ? id : 'warden', x, y);
    this.boss = b;
    this.ctx.enemies.register(b);
    this.ctx.bus.emit('boss:spawned', { boss: b });
    b.intro();
    return b;
  }

  /** Called when the death sequence completes. */
  finish(b: Boss): void {
    this.ctx.enemies.unregister(b);
  }

  update(dt: number): void {
    this.boss?.update(dt);
  }

  destroy(): void {
    this.subs.dispose();
    this.boss?.destroy();
    this.boss = null;
  }
}

export function createBosses(ctx: GameContext): BossSystemApi {
  return new BossSystem(ctx);
}
